<div class="rg-page-header">
    <div>
        <h3 class="fw-bold mb-1">Dashboard</h3>
        <p class="text-muted mb-0">Monitor organizations, licences, API access and validations.</p>
    </div>

    <div class="d-flex gap-2">
        <button class="btn btn-outline-secondary" (click)="refreshDashboard()" [disabled]="loading">
            <i class="bi bi-arrow-clockwise me-1"></i>
            <span *ngIf="!loading">Refresh</span>
            <span *ngIf="loading">Loading...</span>
        </button>

        <button class="btn btn-primary" routerLink="/organizations" [queryParams]="{ create: 'true' }">
            <i class="bi bi-plus-lg me-1"></i>
            New Organization
        </button>
    </div>
</div>

<div class="container-fluid p-4">
    <div class="row g-3 mb-4">
        <div class="col-12 col-md-6 col-xl">
            <div class="rg-stat-card">
                <div class="rg-stat-icon bg-primary-subtle text-primary">
                    <i class="bi bi-buildings-fill"></i>
                </div>
                <div>
                    <small class="text-muted">Organizations</small>
                    <h3>{{ summary.totalOrganizations }}</h3>
                </div>
            </div>
        </div>

        <div class="col-12 col-md-6 col-xl">
            <div class="rg-stat-card">
                <div class="rg-stat-icon bg-success-subtle text-success">
                    <i class="bi bi-patch-check-fill"></i>
                </div>
                <div>
                    <small class="text-muted">Active Licences</small>
                    <h3>{{ summary.activeLicences }}</h3>
                </div>
            </div>
        </div>

        <div class="col-12 col-md-6 col-xl">
            <div class="rg-stat-card">
                <div class="rg-stat-icon bg-warning-subtle text-warning">
                    <i class="bi bi-hourglass-split"></i>
                </div>
                <div>
                    <small class="text-muted">Expiring Soon</small>
                    <h3>{{ summary.expiringIn30Days }}</h3>
                </div>
            </div>
        </div>

        <div class="col-12 col-md-6 col-xl">
            <div class="rg-stat-card">
                <div class="rg-stat-icon bg-danger-subtle text-danger">
                    <i class="bi bi-x-octagon-fill"></i>
                </div>
                <div>
                    <small class="text-muted">Failed Validations</small>
                    <h3>{{ summary.failedValidationRequests }}</h3>
                </div>
            </div>
        </div>

        <div class="col-12 col-md-6 col-xl">
            <div class="rg-stat-card">
                <div class="rg-stat-icon bg-info-subtle text-info">
                    <i class="bi bi-shield-lock-fill"></i>
                </div>
                <div>
                    <small class="text-muted">Password Requests</small>
                    <h3>{{ summary.pendingPasswordResetRequests }}</h3>
                    <a routerLink="/password-reset-requests" class="small text-decoration-none">
                        Review requests
                    </a>
                </div>
            </div>
        </div>
    </div>

    <div class="row g-3 mb-4">
        <div class="col-12 col-xl-8">
            <div class="card border-0 shadow-sm rg-card">
                <div class="card-header bg-white d-flex justify-content-between align-items-center">
                    <strong>Licence Overview</strong>
                    <span class="badge text-bg-light">Live summary</span>
                </div>

                <div class="card-body">
                    <div class="row g-3">
                        <div class="col-6 col-md-3">
                            <small class="text-muted">Total</small>
                            <h5 class="fw-bold">{{ summary.totalLicences }}</h5>
                        </div>

                        <div class="col-6 col-md-3">
                            <small class="text-muted">Expired</small>
                            <h5 class="fw-bold">{{ summary.expiredLicences }}</h5>
                        </div>

                        <div class="col-6 col-md-3">
                            <small class="text-muted">Suspended</small>
                            <h5 class="fw-bold">{{ summary.suspendedLicences }}</h5>
                        </div>

                        <div class="col-6 col-md-3">
                            <small class="text-muted">Revoked</small>
                            <h5 class="fw-bold">{{ summary.revokedLicences }}</h5>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="col-12 col-xl-4">
            <div class="card border-0 shadow-sm rg-card">
                <div class="card-header bg-white">
                    <strong>API Credentials</strong>
                </div>

                <div class="card-body">
                    <div class="d-flex justify-content-between mb-3">
                        <span>Total Credentials</span>
                        <strong>{{ summary.totalApiCredentials }}</strong>
                    </div>

                    <div class="d-flex justify-content-between mb-3">
                        <span>Active</span>
                        <strong class="text-success">{{ summary.activeApiCredentials }}</strong>
                    </div>

                    <div class="d-flex justify-content-between">
                        <span>Revoked</span>
                        <strong class="text-danger">{{ summary.revokedApiCredentials }}</strong>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div class="row g-3">
        <div class="col-12 col-xl-7">
            <div class="card border-0 shadow-sm rg-card">
                <div class="card-header bg-white d-flex justify-content-between align-items-center">
                    <strong>Validation Requests</strong>
                    <a routerLink="/validation-logs" class="btn btn-sm btn-outline-primary">View Logs</a>
                </div>

                <div class="card-body">
                    <div class="row g-3">
                        <div class="col-md-4">
                            <small class="text-muted">Total Requests</small>
                            <h4 class="fw-bold">{{ summary.totalValidationRequests }}</h4>
                        </div>

                        <div class="col-md-4">
                            <small class="text-muted">Successful</small>
                            <h4 class="fw-bold text-success">{{ summary.successfulValidationRequests }}</h4>
                        </div>

                        <div class="col-md-4">
                            <small class="text-muted">Failed</small>
                            <h4 class="fw-bold text-danger">{{ summary.failedValidationRequests }}</h4>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="col-12 col-xl-5">
            <div class="card border-0 shadow-sm rg-card">
                <div class="card-header bg-white">
                    <strong>Quick Actions</strong>
                </div>

                <div class="card-body d-grid gap-2">
                    <button class="btn btn-outline-primary text-start" routerLink="/organizations">
                        <i class="bi bi-plus-circle me-2"></i>
                        Create Organization
                    </button>

                    <button class="btn btn-outline-secondary text-start" routerLink="/organizations">
                        <i class="bi bi-key me-2"></i>
                        Generate API Credential
                    </button>

                    <button class="btn btn-outline-secondary text-start" routerLink="/organizations">
                        <i class="bi bi-patch-check me-2"></i>
                        Issue Licence
                    </button>

                    <button *ngIf="isSuperAdmin" class="btn btn-outline-secondary text-start" routerLink="/admin-users">
                        <i class="bi bi-person-plus me-2"></i>
                        Create Admin User
                    </button>
                </div>
            </div>
        </div>
    </div>
</div>
<div class="card border-0 shadow-sm rg-card mb-4">
    <div class="card-header bg-white d-flex justify-content-between align-items-center">
        <strong>Licences Expiring Soon</strong>
        <span class="badge text-bg-warning">Next 30 days</span>
    </div>

    <div class="card-body p-0">
        <table class="table align-middle mb-0">
            <thead>
                <tr>
                    <th>Organization</th>
                    <th>Licence Key</th>
                    <th>Expires</th>
                    <th>Days Left</th>
                </tr>
            </thead>

            <tbody>
                <tr *ngIf="expiringLicences.length === 0">
                    <td colspan="4" class="text-center text-muted py-4">
                        No record found.
                    </td>
                </tr>

                <tr *ngFor="let licence of expiringLicences">
                    <td>
                        <div class="fw-semibold">{{ licence.organizationName }}</div>
                        <small class="text-muted">{{ licence.organizationId }}</small>
                    </td>

                    <td>{{ licence.licenceKey }}</td>

                    <td>{{ licence.expiresAt | date:'mediumDate' }}</td>

                    <td>
                        <span class="badge"
                            [ngClass]="licence.daysRemaining <= 7 ? 'text-bg-danger' : 'text-bg-warning'">
                            {{ licence.daysRemaining }} day(s)
                        </span>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</div>
<div class="row g-3 mt-1">
    <div class="col-12 col-xl-6">
        <div class="card border-0 shadow-sm rg-card">
            <div class="card-header bg-white d-flex justify-content-between align-items-center">
                <strong>Recent Validation Requests</strong>
                <a routerLink="/validation-logs" class="btn btn-sm btn-outline-primary">View All</a>
            </div>

            <div class="card-body p-0">
                <table class="table align-middle mb-0">
                    <thead>
                        <tr>
                            <th>Status</th>
                            <th>Client ID</th>
                            <th>Machine</th>
                            <th>Checked</th>
                        </tr>
                    </thead>

                    <tbody>
                        <tr *ngIf="recentValidationLogs.length === 0">
                            <td colspan="4" class="text-center text-muted py-4">
                                No record found.
                            </td>
                        </tr>

                        <tr *ngFor="let log of recentValidationLogs">
                            <td>
                                <span class="badge"
                                    [ngClass]="log.wasSuccessful ? 'text-bg-success' : 'text-bg-danger'">
                                    {{ log.wasSuccessful ? 'Success' : 'Failed' }}
                                </span>
                            </td>

                            <td>
                                <div class="fw-semibold">{{ log.clientId }}</div>
                                <small class="text-muted">{{ log.deploymentId || 'No deployment' }}</small>
                            </td>

                            <td>{{ log.machineId || '—' }}</td>

                            <td>{{ log.checkedAt | date:'short' }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>

    <div class="col-12 col-xl-6">
        <div class="card border-0 shadow-sm rg-card">
            <div class="card-header bg-white d-flex justify-content-between align-items-center">
                <strong>Recent Audit Activity</strong>
                <a routerLink="/audit-logs" class="btn btn-sm btn-outline-primary">View All</a>
            </div>

            <div class="card-body p-0">
                <table class="table align-middle mb-0">
                    <thead>
                        <tr>
                            <th>Admin</th>
                            <th>Entity</th>
                            <th>Description</th>
                            <th>Time</th>
                        </tr>
                    </thead>

                    <tbody>
                        <tr *ngIf="recentAuditLogs.length === 0">
                            <td colspan="4" class="text-center text-muted py-4">
                                No record found.
                            </td>
                        </tr>

                        <tr *ngFor="let log of recentAuditLogs">
                            <td>
                                <div class="fw-semibold">{{ log.adminEmail || 'System' }}</div>
                            </td>

                            <td>{{ log.entityName }}</td>

                            <td>{{ log.description }}</td>

                            <td>{{ log.createdAt | date:'short' }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</div>
<div class="rg-stat-card">
    <div class="rg-stat-value">
        {{summary.pendingPasswordResetRequests}}
    </div>

    <div class="rg-stat-label">
        Pending Password Requests
    </div>

    <a routerLink="/password-reset-requests" class="btn btn-sm btn-outline-primary mt-2">
        Review Requests
    </a>
</div>
